import { Frame, Navigation } from '@shopify/polaris';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  path: string;
  /** Emoji shown before the label to give the nav a playful, gamified feel. */
  icon: string;
}

/** Sidebar sections per docs/ui/admin-dashboard.md. */
const PRIMARY_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: '🏠' },
  { label: 'Campaigns', path: '/campaigns', icon: '🚀' },
  { label: 'Games', path: '/games', icon: '🎮' },
  { label: 'Rewards', path: '/rewards', icon: '🎁' },
  { label: 'Coupons', path: '/coupons', icon: '🎟️' },
  { label: 'Analytics', path: '/analytics', icon: '📈' },
  { label: 'Customers', path: '/customers', icon: '👥' },
  { label: 'Assets', path: '/assets', icon: '🖼️' },
  { label: 'Themes', path: '/themes', icon: '🎨' },
];

const SECONDARY_ITEMS: NavItem[] = [
  { label: 'Developers', path: '/developers', icon: '🛠️' },
  { label: 'Billing', path: '/billing', icon: '💳' },
  { label: 'Settings', path: '/settings', icon: '⚙️' },
  { label: 'Help & Guide', path: '/help', icon: '📖' },
  { label: 'Support', path: '/support', icon: '💬' },
];

export function AppFrame({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  const toItem = (item: NavItem) => ({
    // Polaris nav labels are plain text, so a leading emoji reads as an icon.
    label: `${item.icon}  ${item.label}`,
    url: item.path,
    selected:
      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path),
    onClick: () => navigate(item.path),
  });

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section items={PRIMARY_ITEMS.map(toItem)} />
      <Navigation.Section title="Account" items={SECONDARY_ITEMS.map(toItem)} separator />
    </Navigation>
  );

  return <Frame navigation={navigationMarkup}>{children}</Frame>;
}
